/**
 * The Azure runner job's command (IM-46): the Azure counterpart of
 * `RUNNER_WRAPPER`, as a shell string the runner template puts in the job's
 * `command`.
 *
 * It does what the AWS wrapper does, in the same order and for the same
 * reasons, with Azure's identities in place of IAM's:
 *
 * 1. **Reads the deploy secret as the runner identity**, from the vault the
 *    runner template made. The secret is not a job secret, so a principal that
 *    can list the job's secrets still cannot see it (IM-45). The runner
 *    identity is then dropped from the environment, so nothing the installer
 *    runs can sign in as it.
 * 2. **Signs in as the deploy app**, into an empty `AZURE_CONFIG_DIR`. The
 *    installer reuses an `az` session already on the right subscription
 *    (installer contract, *Credentials*), so it never runs `az login` with the
 *    secret on its own argv, where an error could echo it. Terraform takes the
 *    same secret from `ARM_CLIENT_SECRET` in the environment. Entra refuses the
 *    secret after its `endDateTime`, which is the grant.
 * 3. **Writes `config.json` with no secret in it**: IM-47's document, with
 *    `ARM_CLIENT_SECRET` `""` and every value in `account_config` taken from
 *    this subscription. `runner-wrapper.test.ts` holds what it writes to
 *    `buildAzureTerraformConfig`'s output for the same inputs, key for key.
 * 4. **Pipes the installer through the same redactor**, self-tested first, and
 *    applies IM-80's marker check. Both are the AWS wrapper's code, from the
 *    same `REDACTOR_SOURCE` and `COMPLETION_MARKERS`, not copies.
 * 5. **Prints one result line last** (`RUN_RESULT_MARKER`), from an exit trap
 *    so a run that fails anywhere still says how. The poll reads it for a
 *    failed execution, because a job execution carries no exit code.
 *
 * Where AWS and Azure differ, and why:
 *
 * - **No assume-role and no hour ceiling.** azurerm and `az` refresh their
 *   tokens from the secret, so a run lasts as long as it needs to — until the
 *   secret expires, at which point it is cut off at its next token. That is
 *   AWS v15's case on every Azure run, which is why the dispatcher and the
 *   start path hold the run to `RUN_CEILING_HOURS` of grant.
 * - **No lock table.** The azurerm backend leases the state blob.
 * - **The state's storage account is found here, by its tags**, unless the
 *   dispatch names one. Its name carries a hash ARM computed, and the control
 *   plane does not read the account on the start path; the deploy app can see
 *   exactly the instance groups it holds, so the tags `qrvey-id` and
 *   `qrvey-purpose` pick out one account or none.
 * - **`location` is the run's region, always.** A config without it deploys to
 *   East US with no error (installer contract), so it is set from
 *   `QIM_LOCATION`, which the dispatcher fills from the run's recorded region,
 *   and a variable naming another location stops the run.
 * - **The tag is checked again.** The dispatcher's request schema holds the
 *   tag to `INSTALLER_TAG_PATTERN`, but whether Logic Apps enforces `pattern`
 *   is not yet shown (IM-45), so this is the check that is known to run.
 *
 * Only an error's *code* is ever printed by the wrapper itself — an AADSTS
 * number, an ARM code — because its own stderr is outside the redactor.
 */
import { COMPLETION_MARKERS } from '../../runs/completion.js';
import { AZURE_REFUSED_VARIABLES } from '../../deployment/azure-config.js';
import { INSTALLER_TAG_PATTERN } from '../../deployment/run-installer.js';
import { RUN_ID_PATTERN } from '../../runs/run.js';
import { REDACTOR_SOURCE } from '../redaction.js';
import { STATE_CONTAINER_NAME } from './instance-template.js';
import { RUN_RESULT_MARKER } from './run-result.js';
/** The `case` arms for the marker check, generated as the AWS wrapper's are. */
const markerCases = Object.entries(COMPLETION_MARKERS)
    .map(([command, marker]) => `  ${command}) expect='${marker}' ;;`)
    .join('\n');
/** The names IM-47 refuses on Azure, refused again here as a last check. */
const refusedNames = Object.keys(AZURE_REFUSED_VARIABLES)
    .map((name) => JSON.stringify(name))
    .join(', ');
/** The installer's Terraform subcommands that accept `-no-color` (IM-167). */
const NO_COLOR_VERBS = [
    'init',
    'plan',
    'apply',
    'destroy',
    'refresh',
    'output',
    'import',
    'show',
    'validate',
];
/**
 * `INSTALLER_TAG_PATTERN` in POSIX classes. Bash's `=~` uses the platform's
 * regex library, and `\\w` is a GNU extension that macOS's does not promise.
 * `runner-wrapper.test.ts` holds the two to the same answers.
 */
export const BASH_TAG_PATTERN = INSTALLER_TAG_PATTERN.replace('[\\w.-]', '[[:alnum:]_.-]').replace('\\w', '[[:alnum:]_]');
export const AZURE_RUNNER_WRAPPER = `set -euo pipefail

for tool in az jq python3; do
  if ! command -v "$tool" >/dev/null 2>&1; then
    echo "runner: this installer image has no $tool, which the runner needs" >&2
    exit 2
  fi
done

# Overridable only so the tests can run this against a temporary directory.
app="\${QRVEY_APP_DIR:-/app}"

: "\${QIM_RUN_ID:?}" "\${QIM_VARIABLES:?}" "\${QIM_LOCATION:?}"
: "\${QIM_TENANT_ID:?}" "\${QIM_SUBSCRIPTION_ID:?}" "\${QIM_DEPLOY_CLIENT_ID:?}"
: "\${QIM_DEPLOY_SECRET_URI:?}" "\${QIM_RUNNER_CLIENT_ID:?}"

# The run id goes into the result line, so it is held to the control plane's
# own grammar before anything prints it.
if ! [[ $QIM_RUN_ID =~ ${RUN_ID_PATTERN.source} ]]; then
  echo "runner: QIM_RUN_ID is not a run id" >&2
  exit 2
fi

# The result line, printed last whatever happens: the poll reads it for a
# failed execution, because a job execution carries no exit code (IM-46).
# Every value in it is the wrapper's own, never installer output.
report() {
  local status=$?
  printf '${RUN_RESULT_MARKER} {"runId":"%s","exitCode":%d}\\n' "$QIM_RUN_ID" "$status"
}
trap report EXIT

# The installer tag, again. The dispatcher's schema holds it to the same
# pattern, but whether Logic Apps enforces a schema pattern is not yet shown.
if [ -n "\${QIM_IMAGE_TAG:-}" ] && ! [[ $QIM_IMAGE_TAG =~ ${BASH_TAG_PATTERN} ]]; then
  echo "runner: the requested installer tag is not a tag" >&2
  exit 2
fi

# The first AADSTS code, or the first ARM-style code, in an az failure. Never
# the message: this stderr is outside the redactor.
errcode() {
  local out=$1 code
  code=$(printf '%s' "$out" | { grep -oE 'AADSTS[0-9]+' || true; } | head -1)
  [ -n "$code" ] || code=$(printf '%s' "$out" | { grep -oE '\\(([A-Za-z][A-Za-z.]*)\\)' || true; } | head -1 | tr -d '()')
  [ -n "$code" ] || code=$(printf '%s' "$out" | { grep -oE 'Code: [A-Za-z][A-Za-z.]*' || true; } | head -1 | cut -c7-)
  printf '%s' "\${code:-no error code}"
}

# The secrets, read as the runner identity in a profile of its own that is
# deleted straight after -- so the installer's profile never holds it.
#
# The deploy secret, and this run's own secret variables when the dispatcher
# stored any (QIM_RUN_SECRETS_URI, a versioned secret in the same vault). They
# cannot travel in the job's environment, which Reader reads.
runner_profile=$(mktemp -d)
runner_az() { AZURE_CONFIG_DIR="$runner_profile" az "$@"; }
if ! out=$(runner_az login --identity --client-id "$QIM_RUNNER_CLIENT_ID" 2>&1 >/dev/null); then
  # az before 2.69 names the identity --username.
  if ! out=$(runner_az login --identity --username "$QIM_RUNNER_CLIENT_ID" 2>&1 >/dev/null); then
    echo "runner: could not sign in as the runner identity ($(errcode "$out"))" >&2
    exit 2
  fi
fi
if ! deploy_secret=$(runner_az keyvault secret show --id "$QIM_DEPLOY_SECRET_URI" --query value -o tsv 2>/dev/null); then
  echo "runner: could not read the deploy secret from the runner's vault -- run the deploy-access script's grant command" >&2
  exit 2
fi
run_secrets='[]'
if [ -n "\${QIM_RUN_SECRETS_URI:-}" ]; then
  if ! run_secrets=$(runner_az keyvault secret show --id "$QIM_RUN_SECRETS_URI" --query value -o tsv 2>/dev/null); then
    echo "runner: could not read this run's secret variables from the runner's vault" >&2
    exit 2
  fi
  if ! printf '%s' "$run_secrets" | jq -e 'type == "array" and all(.[]; (.path | type) == "array")' >/dev/null 2>&1; then
    echo "runner: this run's secret variables are not in the shape the dispatcher writes" >&2
    exit 2
  fi
fi
runner_az logout >/dev/null 2>&1 || true
rm -rf "$runner_profile"
# The runner identity is not the installer's to use. Without these nothing
# downstream can ask the platform for its tokens.
unset IDENTITY_ENDPOINT IDENTITY_HEADER MSI_ENDPOINT MSI_SECRET

# The run's variables: the dispatch's, with this run's secrets put back where
# they were taken from, and the registry token this account already holds
# (IM-118) when the run did not send one.
vars=$(printf '%s' "$QIM_VARIABLES" | jq -c --argjson s "$run_secrets" \\
  'reduce $s[] as $e (.; setpath($e.path; $e.value))')
unset run_secrets
vars=$(printf '%s' "$vars" | jq -c \\
  --arg key "\${QIM_REGISTRY_KEY:-}" --arg user "\${QIM_REGISTRY_USER:-}" \\
  '(if (.registry_key // "") == "" and $key != "" then . + {registry_key: $key} else . end)
   | (if (.registry_user // "") == "" and $user != "" then . + {registry_user: $user} else . end)')

# Names the Azure installer must never be sent (IM-47): the control plane
# refuses them first, and this is the last place before the container runs.
refused=$(printf '%s' "$vars" | jq -r '[${refusedNames}] as $no | keys[] | select(. as $k | $no | index($k))' | head -1)
if [ -n "$refused" ]; then
  echo "runner: $refused cannot be sent to the Azure installer" >&2
  exit 2
fi

# The region, always the run's own. Absent, the installer deploys to East US
# without an error; a variable naming another location is a run that would
# build where its record does not say.
given_location=$(printf '%s' "$vars" | jq -r '.location // empty')
if [ -n "$given_location" ] && [ "$given_location" != "$QIM_LOCATION" ]; then
  echo "runner: variables.location ($given_location) is not this run's region ($QIM_LOCATION)" >&2
  exit 2
fi
vars=$(printf '%s' "$vars" | jq -c --arg location "$QIM_LOCATION" '. + {location: $location}')

# Sent on every run since IM-147: the id the control plane records it under.
deployment_id=$(printf '%s' "$vars" | jq -r '.deployment_id // empty')
if [ -z "$deployment_id" ]; then
  echo "runner: the run carries no deployment_id" >&2
  exit 2
fi
# It goes into a query below, and every id the control plane sends is this
# shape (azureDeploymentIdProblem), which is also the one the installer's
# lowercase-and-strip rewrite leaves alone.
if ! [[ $deployment_id =~ ^[a-z0-9]+$ ]]; then
  echo "runner: deployment_id may contain only lowercase letters and digits" >&2
  exit 2
fi

# Signed in as the deploy app, into the installer's own empty profile.
export ARM_CLIENT_ID="$QIM_DEPLOY_CLIENT_ID" ARM_TENANT_ID="$QIM_TENANT_ID"
export ARM_SUBSCRIPTION_ID="$QIM_SUBSCRIPTION_ID" ARM_CLIENT_SECRET="$deploy_secret"
unset deploy_secret
mkdir -p "\${AZURE_CONFIG_DIR:=/tmp/qim-az}"
export AZURE_CONFIG_DIR
if ! out=$(az login --service-principal --username "$ARM_CLIENT_ID" --password "$ARM_CLIENT_SECRET" \\
  --tenant "$ARM_TENANT_ID" 2>&1 >/dev/null); then
  code=$(errcode "$out")
  case "$code" in
    AADSTS7000222) echo "runner: the deploy app's secret has expired ($code), so deploy access has ended -- run the deploy-access script's grant command" >&2 ;;
    *) echo "runner: could not sign in as the deploy app ($code)" >&2 ;;
  esac
  exit 2
fi
if ! out=$(az account set --subscription "$ARM_SUBSCRIPTION_ID" 2>&1); then
  echo "runner: the deploy app cannot see subscription $ARM_SUBSCRIPTION_ID ($(errcode "$out"))" >&2
  exit 2
fi

# Where the state lives: named by the dispatch (an adopted instance's, as
# resource-group/account/container), or the instance template's, found by its
# tags among the storage accounts the deploy app can see -- exactly the
# instance groups it was given.
state_rg='' state_account='' state_container='${STATE_CONTAINER_NAME}'
if [ -n "\${QIM_STATE_STORE:-}" ]; then
  IFS=/ read -r state_rg state_account state_container <<<"$QIM_STATE_STORE"
  if [ -z "$state_rg" ] || [ -z "$state_account" ] || [ -z "$state_container" ]; then
    echo "runner: QIM_STATE_STORE is not resource-group/account/container" >&2
    exit 2
  fi
else
  if ! found=$(az storage account list \\
    --query "[?tags.\\"qrvey-id\\"=='$deployment_id' && tags.\\"qrvey-purpose\\"=='terraform-state'].[resourceGroup, name]" \\
    -o tsv 2>&1); then
    echo "runner: could not list the storage accounts the deploy app can see ($(errcode "$found"))" >&2
    exit 2
  fi
  case $(printf '%s' "$found" | grep -c . || true) in
    1) read -r state_rg state_account <<<"$found" ;;
    0)
      echo "runner: no state storage for instance $deployment_id -- deploy its instance template before its first run" >&2
      exit 2 ;;
    *)
      echo "runner: more than one storage account is tagged as instance $deployment_id's state, so this run will not guess" >&2
      exit 2 ;;
  esac
fi
state_key="\${QIM_STATE_KEY:-}"
[ -n "$state_key" ] || state_key="$deployment_id/state.tfstate"

# IM-47's document. account_config holds no secret: ARM_CLIENT_SECRET is ""
# and the installer takes the value from the environment, where it already is.
printf '%s' "$vars" | jq \\
  --arg client "$ARM_CLIENT_ID" --arg tenant "$ARM_TENANT_ID" --arg sub "$ARM_SUBSCRIPTION_ID" \\
  --arg rg "$state_rg" --arg account "$state_account" --arg container "$state_container" \\
  --arg key "$state_key" \\
  '{_metadata: {cloud: "azure"},
    account_config: {ARM_CLIENT_ID: $client, ARM_CLIENT_SECRET: "", ARM_TENANT_ID: $tenant,
      ARM_SUBSCRIPTION_ID: $sub, resource_group_name: $rg, storage_account_name: $account,
      container_name: $container, key: $key},
    variables: .}' > "$app/config.json"
unset vars

# The cloud stated, rather than inferred from which credential fields exist.
export QRVEY_CLOUD=azure

# No terminal colour in the log (IM-167), as on AWS. Appended to, never replaced.
${NO_COLOR_VERBS.map((verb) => `export TF_CLI_ARGS_${verb}="\${TF_CLI_ARGS_${verb}:+$TF_CLI_ARGS_${verb} }-no-color"`).join('\n')}
export NO_COLOR=1 TERM=dumb

# Redaction, written out here so the customer reads in this template exactly
# what is masked before anything is logged. The AWS runner's rules, unchanged.
cat > "$app/qrvey-redact.py" <<'QRVEY_REDACTOR'
${REDACTOR_SOURCE}QRVEY_REDACTOR

# Fail closed: a seeded secret that survives the rules stops the run here.
python3 "$app/qrvey-redact.py" --self-test

expect=''
case "\${1:-}" in
${markerCases}
esac

set +e
"$app/entrypoint.sh" "$@" 2>&1 | python3 -u "$app/qrvey-redact.py" | awk -v m="$expect" '
  { print; fflush() }
  m != "" && index($0, m) { seen = 1 }
  END { exit((m == "" || seen) ? 0 : 3) }
'
# One assignment: PIPESTATUS is rebuilt by every command (see the AWS wrapper).
statuses=(\${PIPESTATUS[@]})
installer_status=\${statuses[0]}
redactor_status=\${statuses[1]}
marker_status=\${statuses[2]}
set -e

if [ "$installer_status" -ne 0 ]; then
  exit "$installer_status"
fi
# A redactor that died took the log with it; the run is not known to be clean.
if [ "$redactor_status" -ne 0 ]; then
  echo "qrvey-wrapper: the redactor stopped (exit $redactor_status), so this run's log is incomplete" >&2
  exit 4
fi
# IM-80: an exit 0 with no completion line did not finish.
if [ "$marker_status" -ne 0 ]; then
  echo "qrvey-wrapper: exited 0 but never printed [$expect] -- treating as failed (IM-80)" >&2
  exit 3
fi
exit 0
`;
//# sourceMappingURL=runner-wrapper.js.map